import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, CheckCircle, XCircle, Eye, Building2, Package } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface PartnerProfile {
  id: string;
  user_id: string;
  company_name: string;
  company_description: string | null;
  website_url: string | null;
  contact_email: string;
  is_verified: boolean;
  created_at: string;
}

interface PartnerOffer {
  id: string;
  partner_id: string;
  title: string;
  description: string;
  offer_type: string;
  price: number | null;
  discount_percentage: number | null;
  status: string;
  is_active: boolean;
  created_at: string;
  partner_profiles: PartnerProfile;
}

const AdminPartners = () => {
  const [offers, setOffers] = useState<PartnerOffer[]>([]);
  const [partners, setPartners] = useState<PartnerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState<PartnerOffer | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch all offers with partner info
      const { data: offersData, error: offersError } = await supabase
        .from("partner_offers")
        .select(`
          *,
          partner_profiles (
            id,
            user_id,
            company_name,
            company_description,
            website_url,
            contact_email,
            is_verified,
            created_at
          )
        `)
        .order("created_at", { ascending: false });

      if (offersError) throw offersError;
      setOffers(offersData || []);

      // Fetch all partners
      const { data: partnersData, error: partnersError } = await supabase
        .from("partner_profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (partnersError) throw partnersError;
      setPartners(partnersData || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load partner data");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveOffer = async (offerId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from("partner_offers")
        .update({
          status: "approved",
          approved_by: user.id,
          approved_at: new Date().toISOString(),
        })
        .eq("id", offerId);

      if (error) throw error;
      toast.success("Offer approved successfully");
      fetchData();
    } catch (error) {
      console.error("Error approving offer:", error);
      toast.error("Failed to approve offer");
    }
  };

  const handleRejectOffer = async () => {
    if (!selectedOffer) return;

    try {
      const { error } = await supabase
        .from("partner_offers")
        .update({
          status: "rejected",
          rejection_reason: rejectionReason,
        })
        .eq("id", selectedOffer.id);

      if (error) throw error;
      toast.success("Offer rejected");
      setShowRejectDialog(false);
      setRejectionReason("");
      setSelectedOffer(null);
      fetchData();
    } catch (error) {
      console.error("Error rejecting offer:", error);
      toast.error("Failed to reject offer");
    }
  };

  const handleVerifyPartner = async (partnerId: string, verify: boolean) => {
    try {
      const { error } = await supabase
        .from("partner_profiles")
        .update({ is_verified: verify })
        .eq("id", partnerId);

      if (error) throw error;
      toast.success(verify ? "Partner verified" : "Partner verification removed");
      fetchData();
    } catch (error) {
      console.error("Error updating partner:", error);
      toast.error("Failed to update partner");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-500";
      case "pending":
        return "bg-yellow-500";
      case "rejected":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  const formatPrice = (cents: number | null) => {
    if (!cents) return "Free";
    return `$${(cents / 100).toFixed(2)}`;
  };

  const pendingOffers = offers.filter(o => o.status === "pending");
  const approvedOffers = offers.filter(o => o.status === "approved");
  const rejectedOffers = offers.filter(o => o.status === "rejected");

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard/admin">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-4xl font-bold">Partner Management</h1>
            <p className="text-muted-foreground mt-2">
              Manage partner profiles and review offers
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Partners
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{partners.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pending Offers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{pendingOffers.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Approved Offers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{approvedOffers.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Offers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{offers.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList>
            <TabsTrigger value="pending" className="gap-2">
              <Eye className="h-4 w-4" />
              Pending ({pendingOffers.length})
            </TabsTrigger>
            <TabsTrigger value="approved" className="gap-2">
              <CheckCircle className="h-4 w-4" />
              Approved ({approvedOffers.length})
            </TabsTrigger>
            <TabsTrigger value="rejected" className="gap-2">
              <XCircle className="h-4 w-4" />
              Rejected ({rejectedOffers.length})
            </TabsTrigger>
            <TabsTrigger value="partners" className="gap-2">
              <Building2 className="h-4 w-4" />
              Partners ({partners.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-4">
            {pendingOffers.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No pending offers to review
                </CardContent>
              </Card>
            ) : (
              pendingOffers.map((offer) => (
                <Card key={offer.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{offer.title}</CardTitle>
                        <CardDescription className="mt-2">
                          By {offer.partner_profiles.company_name} • {new Date(offer.created_at).toLocaleDateString()}
                        </CardDescription>
                      </div>
                      <Badge className={getStatusColor(offer.status)}>{offer.status}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Description:</p>
                      <p>{offer.description}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Type:</p>
                        <Badge variant="outline" className="capitalize">{offer.offer_type}</Badge>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Price:</p>
                        <p className="font-semibold">
                          {formatPrice(offer.price)}
                          {offer.discount_percentage && (
                            <span className="text-green-600 ml-2">-{offer.discount_percentage}%</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-4">
                      <Button
                        onClick={() => handleApproveOffer(offer.id)}
                        className="flex-1 gap-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedOffer(offer);
                          setShowRejectDialog(true);
                        }}
                        variant="destructive"
                        className="flex-1 gap-2"
                      >
                        <XCircle className="h-4 w-4" />
                        Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="approved" className="space-y-4">
            {approvedOffers.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No approved offers yet
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {approvedOffers.map((offer) => (
                  <Card key={offer.id}>
                    <CardHeader>
                      <CardTitle className="text-lg">{offer.title}</CardTitle>
                      <CardDescription>
                        By {offer.partner_profiles.company_name}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                        {offer.description}
                      </p>
                      <div className="flex justify-between items-center">
                        <Badge variant="outline" className="capitalize">{offer.offer_type}</Badge>
                        <span className="font-semibold">{formatPrice(offer.price)}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="rejected" className="space-y-4">
            {rejectedOffers.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No rejected offers
                </CardContent>
              </Card>
            ) : (
              rejectedOffers.map((offer) => (
                <Card key={offer.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{offer.title}</CardTitle>
                        <CardDescription>By {offer.partner_profiles.company_name}</CardDescription>
                      </div>
                      <Badge className={getStatusColor(offer.status)}>{offer.status}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{offer.description}</p>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="partners" className="space-y-4">
            {partners.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-muted-foreground">
                  No partners yet
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {partners.map((partner) => (
                  <Card key={partner.id}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle>{partner.company_name}</CardTitle>
                          <CardDescription>{partner.contact_email}</CardDescription>
                        </div>
                        {partner.is_verified && (
                          <Badge className="bg-green-500">Verified</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {partner.company_description && (
                        <p className="text-sm">{partner.company_description}</p>
                      )}
                      {partner.website_url && (
                        <a
                          href={partner.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline"
                        >
                          Visit Website
                        </a>
                      )}
                      <Button
                        variant={partner.is_verified ? "outline" : "default"}
                        size="sm"
                        onClick={() => handleVerifyPartner(partner.id, !partner.is_verified)}
                        className="w-full"
                      >
                        {partner.is_verified ? "Remove Verification" : "Verify Partner"}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Rejection Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Offer</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this offer. The partner will be notified.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Reason for rejection..."
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectOffer}
              disabled={!rejectionReason.trim()}
            >
              Reject Offer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPartners;
